import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, X } from "lucide-react";
import { Carregando, Falha } from "../shell/Pagina.js";
import { trpc } from "../lib/trpc.js";
import { moeda, competencia as fComp } from "../lib/formato.js";

/**
 * Nó da memória de cálculo, como o motor produz.
 *
 * A árvore vem do próprio avaliador: cada nó do AST produz o seu nó de memória
 * DURANTE a avaliação. Não existe gerador de memória separado que possa
 * divergir do cálculo, e se existisse seria pior que não ter.
 */
type No = {
  rotulo: string;
  formula?: string;
  valor?: number;
  fonte?: string;
  filhos?: No[];
};

export function Holerite(
  { competenciaId, vinculoId, aoFechar }:
  { competenciaId: string; vinculoId: string; aoFechar: () => void },
) {
  const q = useQuery({
    queryKey: ["folha", "holerite", competenciaId, vinculoId],
    queryFn: () => trpc.fechamento.holerite.query({ competenciaId, vinculoId }),
  });

  const h = q.data;
  const proventos = h?.itens.filter((i) => i.tipo === "provento") ?? [];
  const descontos = h?.itens.filter((i) => i.tipo === "desconto") ?? [];

  return (
    <div className="eop-modal-fundo" role="dialog" aria-modal="true"
         aria-label="Demonstrativo de pagamento"
         onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="eop-modal" style={{ maxWidth: "46rem" }}>
        <header className="painel-topo flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[0.9375rem] font-semibold">
              {h ? h.nome : "Demonstrativo de pagamento"}
            </h2>
            {h && (
              <p className="mt-1 text-xs" style={{ color: "var(--faint-foreground)" }}>
                matrícula {h.matricula}
                {h.cargo && ` · ${h.cargo}`} · {fComp(h.competencia)} · {h.tipo}
              </p>
            )}
          </div>
          <button className="botao-icone" aria-label="Fechar" onClick={aoFechar}>
            <X aria-hidden size={16} />
          </button>
        </header>

        <div className="painel-corpo">
          {q.isPending && <Carregando linhas={6} nu />}
          {q.isError && <Falha erro={q.error} tentarDeNovo={() => void q.refetch()} />}

          {h && (
            <>
              <Grupo titulo="Proventos" itens={proventos} />
              <Grupo titulo="Descontos" itens={descontos} />

              <dl className="eop-holerite-totais">
                <div><dt>Base do INSS</dt><dd className="num">{moeda(h.baseInss)}</dd></div>
                <div><dt>Base do IRRF</dt><dd className="num">{moeda(h.baseIrrf)}</dd></div>
                {/* FGTS é depósito do empregador, não desconto: aparece como
                    base para o recolhimento e nunca como linha de desconto. */}
                <div><dt>Base do FGTS</dt><dd className="num">{moeda(h.baseFgts)}</dd></div>
                <div className="eop-holerite-liquido">
                  <dt>Líquido a receber</dt><dd className="num">{moeda(h.liquido)}</dd>
                </div>
              </dl>

              <p className="carimbo mt-4">
                clique em qualquer valor para abrir a conta que o produziu
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Grupo(
  { titulo, itens }:
  { titulo: string; itens: { codigo: string; descricao: string; valor: string; memoria: unknown }[] },
) {
  if (itens.length === 0) return null;
  return (
    <section className="eop-holerite-grupo">
      <p className="eyebrow">{titulo}</p>
      <ul className="eop-holerite-itens">
        {itens.map((i) => <Item key={i.codigo + i.valor} item={i} />)}
      </ul>
    </section>
  );
}

function Item({ item }: { item: { codigo: string; descricao: string; valor: string; memoria: unknown } }) {
  const [aberto, setAberto] = useState(false);
  const memoria = item.memoria as No | null;

  return (
    <li>
      <button type="button" className="eop-holerite-linha"
              aria-expanded={aberto} disabled={!memoria}
              onClick={() => setAberto((v) => !v)}>
        <ChevronRight aria-hidden size={13} className="eop-holerite-seta"
                      data-aberto={aberto || undefined} />
        <span className="num eop-holerite-codigo">{item.codigo}</span>
        <span className="min-w-0 flex-1 truncate">{item.descricao}</span>
        <span className="num eop-holerite-valor">{moeda(item.valor)}</span>
      </button>
      {aberto && memoria && (
        <div className="eop-memoria">
          <NoMemoria no={memoria} nivel={0} />
        </div>
      )}
    </li>
  );
}

/**
 * A árvore da memória, expansível.
 *
 * Mostra a fórmula com os NÚMEROS já substituídos, não a fórmula genérica:
 * "1.621,00 × 7,50%" explica; "base × alíquota" não explica nada.
 */
function NoMemoria({ no, nivel }: { no: No; nivel: number }) {
  const [aberto, setAberto] = useState(nivel < 1);
  const temFilhos = (no.filhos?.length ?? 0) > 0;

  return (
    <div className="eop-no" style={{ marginLeft: nivel > 0 ? "0.875rem" : 0 }}>
      <button type="button" className="eop-no-cabeca"
              aria-expanded={temFilhos ? aberto : undefined}
              disabled={!temFilhos}
              onClick={() => setAberto((v) => !v)}>
        {temFilhos && (
          <ChevronRight aria-hidden size={11} className="eop-holerite-seta"
                        data-aberto={aberto || undefined} />
        )}
        <span className="eop-no-rotulo">{no.rotulo}</span>
        {no.formula && <code className="eop-no-formula">{no.formula}</code>}
        {no.valor !== undefined && (
          <span className="num eop-no-valor">{moeda((no.valor / 100).toFixed(2))}</span>
        )}
      </button>
      {/* A citação da norma vem do CADASTRO da tabela legal, nunca do código:
          uma medida provisória nova atualiza o texto sem deploy. */}
      {no.fonte && <p className="eop-no-fonte">{no.fonte}</p>}
      {temFilhos && aberto && no.filhos!.map((f, i) => (
        <NoMemoria key={`${f.rotulo}-${i}`} no={f} nivel={nivel + 1} />
      ))}
    </div>
  );
}
